import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUSES = ["new", "contacted", "accepted", "declined"];
const BADGE = { new: "bg-brass/15 text-brass", contacted: "bg-accent/15 text-accent", accepted: "bg-emerald/20 text-emerald", declined: "bg-rule text-mutedink" };

function Row({ a, invalidate }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(a.notes || "");
  const save = async (patch) => {
    try { await api.patch(`/admin/applications/${a.id}`, patch); toast.success("Updated"); invalidate(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="border-b border-rule py-3" data-testid={`application-${a.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 text-left">
        <div>
          <span className="font-heading font-semibold text-ink">{a.name}</span>
          <span className="ml-2 text-sm text-mutedink">{a.business_name} · {a.city} · {a.revenue_band}</span>
        </div>
        <span className={`kicker rounded-[3px] px-2 py-1 text-[10px] ${BADGE[a.status] || ""}`}>{a.status}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-sm text-bodytext">
          <p><b>Email:</b> {a.email} · <b>Phone:</b> {a.phone}</p>
          <p><b>Sells:</b> {a.what_you_sell}</p>
          <p><b>Number they wish they knew:</b> {a.number_you_wish_you_knew}</p>
          <p><b>A week without them:</b> {a.week_without_you}</p>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => save({ status: s })} data-testid={`app-status-${a.id}-${s}`}
                className={`kicker rounded-[3px] border border-rule px-2 py-1 text-[10px] ${a.status === s ? "bg-ink text-paper" : "text-mutedink hover:bg-card"}`}>{s}</button>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" className="flex-1 rounded-[3px] border border-rule bg-background px-3 py-1.5 text-sm" data-testid={`app-note-${a.id}`} />
            <Button size="sm" onClick={() => save({ notes: note })} className="rounded-[3px] bg-ink text-paper">Save note</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ApplicationsAdmin() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-applications"] });
  const { data } = useQuery({ queryKey: ["admin-applications"], queryFn: async () => (await api.get("/admin/applications")).data });
  return (
    <div data-testid="applications-admin">
      <h2 className="font-heading text-xl font-bold text-ink">Applications — The Room</h2>
      <p className="mt-1 text-sm text-mutedink">Newest first. Click a row to expand, change status, or add a note.</p>
      <div className="mt-4 border-t border-rule">
        {(data || []).length === 0 && <p className="py-6 text-mutedink">No applications yet.</p>}
        {(data || []).map((a) => <Row key={a.id} a={a} invalidate={invalidate} />)}
      </div>
    </div>
  );
}
